"use client";

import { Icon, makeStyles, MenuItem, MenuProps, Tab, Tabs, Theme, Typography } from "@mui/material";
import * as React from "react";
import { useGqlClient } from "@/Providers/GQLProvider";
import { useQuery } from "@tanstack/react-query";
import NextLink from "next/link";
import HoverMenu from "material-ui-popup-state/HoverMenu";
import { usePopupState, bindHover, bindMenu, bindFocus, PopupState } from "material-ui-popup-state/hooks";
import Arrow2DownIcon from "@/app/_components/Icons/Arrow2DownIcon";
import Arrow2UpIcon from "@/app/_components/Icons/Arrow2UpIcon";
import { CategoryDto } from "@/__generated__/sdk";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { SyntheticEvent } from "react";
import Arrow2LeftIcon from "@/app/_components/Icons/Arrow2LeftIcon";
type CatType = {
  href: string;
} & CategoryDto;
const CascadingContext = React.createContext<{
  parentPopupState: null | PopupState;
  rootPopupState: null | PopupState;
}>({
  parentPopupState: null,
  rootPopupState: null,
});

function CascadingMenuItem({ item, ...props }) {
  const { rootPopupState } = React.useContext(CascadingContext);
  if (!rootPopupState) throw new Error("must be used inside a CascadingMenu");
  const handleClick = React.useCallback(
    (event: SyntheticEvent | Element | null) => {
      rootPopupState.close(event);
      // if (onClick) onClick(event);
    },
    [rootPopupState],
  );

  return (
    <MenuItem
      color="secondary"
      TouchRippleProps={{ color: "secondary" }}
      component={NextLink}
      sx={{
        "& .MuiTouchRipple-root": {
          color: "secondary.main", // Change ripple color here
        },
      }}
      href={item.href}
      {...props}
      onClick={handleClick}
    />
  );
}

function CascadingSubmenu({ title, popupId, item, ...props }) {
  const { parentPopupState } = React.useContext(CascadingContext);
  const popupState = usePopupState({
    popupId,
    variant: "popover",
    parentPopupState,
  });
  return (
    <React.Fragment>
      <CascadingMenuItem item={item} {...bindHover(popupState)} {...bindFocus(popupState)}>
        <Typography variant="subtitle1">{item.title}</Typography>
        <Icon fontSize={"small"} sx={{ ml: 2 }} color={"secondary"}>
          <Arrow2LeftIcon />
        </Icon>
      </CascadingMenuItem>
      {/*<MenuItem {...bindHover(popupState)} {...bindFocus(popupState)}>*/}
      {/*  <span>{title}</span>*/}
      {/*</MenuItem>*/}
      <CascadingMenu
        {...props}
        classes={{ ...props.classes }}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        popupState={popupState}
      />
    </React.Fragment>
  );
}

function CascadingMenu({ popupState, ...props }) {
  const { rootPopupState } = React.useContext(CascadingContext);
  const context = React.useMemo(
    () => ({
      rootPopupState: rootPopupState || popupState,
      parentPopupState: popupState,
    }),
    [rootPopupState, popupState],
  );

  return (
    <CascadingContext.Provider value={context}>
      <HoverMenu {...props} {...bindMenu(popupState)} />
    </CascadingContext.Provider>
  );
}

function RecursiveItems({ item }: { item: CategoryDto }) {
  if (item.children && item.children.length > 0) {
    return (
      <CascadingSubmenu
        slotProps={{
          paper: {
            sx: { borderRadius: 2, boxShadow: `3px 6px 24px -6px #A1A1A129,-3px 0px 24px -6px #A1A1A11F` },
          },
        }}
        key={item.id}
        popupId={`more${item.slug}`}
        item={item}
      >
        {item.children.map((sub, subIndex) => {
          return <RecursiveItems item={sub} key={subIndex} />;
        })}
      </CascadingSubmenu>
    );
  }
  return (
    <CascadingMenuItem key={item.id} item={item}>
      <Typography variant="subtitle1">{item.title}</Typography>
    </CascadingMenuItem>
  );
}

function Item({ item }: { item: CategoryDto }) {
  const popupState = usePopupState({
    popupId: "demoMenu",
    variant: "popover",
  });
  return (
    <>
      <Tab
        {...bindHover(popupState)}
        sx={{
          whiteSpace: "nowrap",
          // p: 1,
          minHeight: "unset",
          opacity: 1,
          borderRadius: 2,
          "& .MuiTouchRipple-root": {
            color: "secondary.main", // Change ripple color here
          },
        }}
        component={NextLink}
        href={`/categories/${item.slug}`}
        label={
          <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span>{item.title}</span>
            {item.children && item.children.length > 0 && (
              <Icon color="secondary" fontSize="small">
                {popupState.isOpen ? <Arrow2UpIcon /> : <Arrow2DownIcon />}
              </Icon>
            )}
          </Typography>
        }
      ></Tab>

      {item.children && item.children.length > 0 && (
        <CascadingMenu
          popupState={popupState}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: { borderRadius: 2, boxShadow: `3px 6px 24px -6px #A1A1A129,-3px 0px 24px -6px #A1A1A11F` },
            },
          }}
        >
          {item.children.map((sub, index) => {
            return <RecursiveItems item={sub} key={index} />;
          })}
        </CascadingMenu>
      )}
    </>
  );
}
function generateCategoriesWithHref(categories: CategoryDto[], basePath = ""): CatType[] {
  return categories.map((category) => {
    // Construct the current href
    const currentHref = `${basePath}/${category.slug}`;

    // Return a new category object with href and recursively processed children
    return {
      ...category,
      href: currentHref,
      children: category.children ? generateCategoriesWithHref(category.children, currentHref) : [],
    };
  });
}
export default function NavbarCategories() {
  const gqlClient = useGqlClient();
  const { data, isLoading } = useQuery({
    queryKey: [gqlClient.menu.name],
    queryFn: async () => {
      return await gqlClient.menu();
    },
    select(data) {
      return generateCategoriesWithHref(data.menu as CatType[], "/categories");
    },
  });

  return (
    <>
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: "unset", display: "flex", alignItems: "center", flexGrow: 1, color: "grey.800" }}
      >
        {data &&
          data.map((item, index) => {
            return <Item key={index} item={item as any}></Item>;
          })}
      </Tabs>
    </>
  );
}
